import React, { CSSProperties, ReactNode, useCallback, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import styles from './styles/dropdown.module.scss'
import genericStyles from './styles/generic-components.module.scss'
import cx from 'classnames'
import { ReactComponent as ArrowDown } from 'images/icons/icn_arrow-down.svg'
import BtnPrimary from './BtnPrimary'
import BtnSecondary from './BtnSecondary'
import { ClassNamesProps } from 'types/utilities'
import Separator from './Separator'
import i18n from 'constants/strings'
import { PointerDownOutsideEvent } from '@radix-ui/react-dismissable-layer'

interface Context {
  isDropdownOpen: boolean
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
}

interface ControlBtn {
  content?: ReactNode
  onClick: (ctx: Context) => void
  disabled?: boolean
}

type ClassKeys =
  | 'root'
  | 'trigger'
  | 'portal'
  | 'content'
  | 'contentWrapper'
  | 'btnsContainer'
  | 'confirmBtn'
  | 'cancelBtn'
  | 'icon'

interface Props extends ClassNamesProps<ClassKeys> {
  ariaLabelTrigger?: string
  contentTrigger: ReactNode
  children: ReactNode
  withChevron?: boolean
  showClearBtn?: boolean
  confirmBtn?: ControlBtn
  cancelBtn?: ControlBtn
  onBeforeOpenChange?: (open: boolean) => void
  onClear?: () => void
  contentStyle?: CSSProperties
  controls?: { isOpen: boolean; setIsOpen: (open: boolean) => void }
}

const Dropdown = ({
  children,
  ariaLabelTrigger,
  contentTrigger,
  withChevron,
  showClearBtn,
  confirmBtn,
  cancelBtn,
  classNames,
  onBeforeOpenChange,
  onClear,
  contentStyle,
  controls,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const { isOpen: isOpenControlled, setIsOpen: setIsOpenControlled } = controls ?? {}
  const isDropdownOpen = isOpenControlled ?? isOpen

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onBeforeOpenChange?.(open)
      setIsOpenControlled ? setIsOpenControlled(open) : setIsOpen(open)
    },
    [onBeforeOpenChange, setIsOpenControlled]
  )

  const preventCloseOnDragScrollbar = useCallback((event: PointerDownOutsideEvent) => {
    const mouseDownEvent = event.detail.originalEvent
    const isTargetHtml = (mouseDownEvent?.target as HTMLElement)?.tagName === 'HTML'
    if (isTargetHtml) {
      // mouse down over scroll element
      event.preventDefault()
    }
  }, [])

  return (
    <Popover.Root open={isDropdownOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        asChild
        onKeyDown={e => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setIsOpenControlled ? setIsOpenControlled(true) : setIsOpen(true)
          }
        }}
      >
        <button
          className={cx(classNames?.trigger, genericStyles.customDropdown, styles.trigger, {
            '--is-open': isDropdownOpen,
            [styles.withChevron]: withChevron,
          })}
          aria-label={ariaLabelTrigger}
        >
          {contentTrigger}
          {withChevron && (
            <ArrowDown
              className={cx(classNames?.icon, styles.icon, {
                '--is-open': isDropdownOpen,
              })}
              aria-hidden
            />
          )}
        </button>
      </Popover.Trigger>
      <Popover.Content
        onPointerDownOutside={preventCloseOnDragScrollbar}
        align='start'
        className={cx(classNames?.contentWrapper, styles.contentWrapper)}
        sideOffset={4}
        collisionPadding={{ top: 900 }}
        style={contentStyle}
      >
        <div className={cx(classNames?.content, styles.content)}>
          {showClearBtn && (
            <>
              <BtnPrimary border={false} onClick={onClear} className={styles.clearBtn}>
                {i18n.t('strFilterBar.clearFilter')}
              </BtnPrimary>
              <Separator className={styles.separator} />
            </>
          )}
          {children}
        </div>
        {(confirmBtn || cancelBtn) && (
          <div
            className={cx(classNames?.btnsContainer, styles.btnsContainer, {
              [styles.doubleBtn]: !!confirmBtn && !!cancelBtn,
            })}
          >
            {cancelBtn && (
              <BtnSecondary
                size='small'
                onClick={() => {
                  cancelBtn.onClick({
                    isDropdownOpen: isDropdownOpen,
                    setIsDropdownOpen: setIsOpen,
                  })
                }}
                className={cx(classNames?.cancelBtn)}
                isDisabled={cancelBtn?.disabled}
              >
                {cancelBtn.content || i18n.t('strCommonUI.button.cancel')}
              </BtnSecondary>
            )}
            {confirmBtn && (
              <BtnPrimary
                size='small'
                onClick={() => {
                  confirmBtn.onClick({
                    isDropdownOpen: isDropdownOpen,
                    setIsDropdownOpen: setIsOpen,
                  })
                }}
                className={cx(classNames?.confirmBtn)}
                isDisabled={confirmBtn?.disabled}
              >
                {confirmBtn.content || i18n.t('strCommonUI.button.applyFilter')}
              </BtnPrimary>
            )}
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  )
}

export default Dropdown
